import { FIREBALL, INK, PALETTE } from '@blorbs/common';
import type { FireballState } from '@blorbs/common';
import type { Camera } from './camera';
import { circle, inkStroke } from './art';

const FIRE = PALETTE[3]; // orange
const CORE = PALETTE[4]; // butter
const EMBER = PALETTE[2]; // coral

/** How much of the path the flame covers, in seconds (~5 head-diameters at fireball speed), and how long a trail lingers after its fireball is gone. */
const TRAIL_SECONDS = 0.19;
const LINGER_SECONDS = 0.22;

interface Sample {
  x: number;
  y: number;
  t: number; // seconds
}

interface Trail {
  pts: Sample[]; // newest first
  /** when the fireball disappeared, or null while alive */
  diedAt: number | null;
  seed: number;
  /** head size factor (fireballs shrink near the end of their range) */
  scale: number;
}

interface Ember {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
  maxLife: number;
  color: string;
}

/**
 * Flame trails, in the picture-book style: a flat orange ribbon with a
 * butter core and a thin ink edge, tapering to a point behind the head. The
 * ribbon follows the fireball's real on-screen path (so it stays glued to it
 * through prediction and handoffs), its edges lick like a flame, and embers
 * peel off the tail. When a fireball dies the ribbon dissipates rather than
 * vanishing.
 */
export class FireballTrails {
  private trails = new Map<number, Trail>();
  private embers: Ember[] = [];

  /** Call once per frame for every fireball being drawn. */
  record(f: FireballState, now: number): void {
    let t = this.trails.get(f.id);
    if (!t) {
      t = { pts: [], diedAt: null, seed: (f.id * 7919) % 97, scale: 1 };
      this.trails.set(f.id, t);
    }
    t.scale = 0.55 + 0.45 * Math.min(1, Math.max(0.15, f.left / 120)); // matches the head's range fade
    const head = t.pts[0];
    // Skip duplicate samples (a paused frame) so the ribbon doesn't get a knot.
    if (!head || Math.hypot(head.x - f.x, head.y - f.y) > 0.5) t.pts.unshift({ x: f.x, y: f.y, t: now });
    while (t.pts.length > 2 && now - t.pts[t.pts.length - 1]!.t > TRAIL_SECONDS) t.pts.pop();

    // Embers: a few sparks peel off the tail and drift back.
    if (t.pts.length > 3 && Math.random() < 0.3) {
      const p = t.pts[Math.min(3, t.pts.length - 1)]!;
      const side = Math.random() < 0.5 ? -1 : 1;
      const life = 0.25 + Math.random() * 0.2;
      this.embers.push({
        x: p.x,
        y: p.y,
        vx: -f.dy * side * 55 - f.dx * 40,
        vy: f.dx * side * 55 - f.dy * 40,
        r: 1.6 + Math.random() * 1.6,
        life,
        maxLife: life,
        color: Math.random() < 0.6 ? CORE : FIRE,
      });
    }
  }

  /** A predicted fireball became a real one: keep its ribbon instead of starting over. */
  transfer(fromId: number, toId: number): void {
    const t = this.trails.get(fromId);
    if (!t) return;
    this.trails.delete(fromId);
    if (!this.trails.has(toId)) this.trails.set(toId, t);
  }

  /** Mark trails whose fireball is gone; they fade out over LINGER_SECONDS. */
  prune(liveIds: Set<number>, now: number): void {
    for (const [id, t] of this.trails) {
      if (liveIds.has(id)) t.diedAt = null;
      else if (t.diedAt === null) t.diedAt = now;
      else if (now - t.diedAt > LINGER_SECONDS) this.trails.delete(id);
    }
  }

  update(dt: number): void {
    for (const e of this.embers) {
      e.x += e.vx * dt;
      e.y += e.vy * dt;
      e.life -= dt;
    }
    this.embers = this.embers.filter((e) => e.life > 0);
  }

  /** Draw all ribbons; call before the fireball heads so the head sits on top. */
  draw(ctx: CanvasRenderingContext2D, cam: Camera, now: number): void {
    for (const t of this.trails.values()) this.drawRibbon(ctx, cam, t, now);
    for (const e of this.embers) {
      const k = e.life / e.maxLife;
      ctx.globalAlpha = Math.min(1, k * 1.6);
      ctx.fillStyle = e.color;
      circle(ctx, e.x, e.y, e.r * (0.5 + k * 0.5));
      ctx.fill();
      inkStroke(ctx, 1.2, cam.scale, INK);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private drawRibbon(ctx: CanvasRenderingContext2D, cam: Camera, t: Trail, now: number): void {
    const pts = t.pts;
    if (pts.length < 2) return;
    const fade = t.diedAt === null ? 1 : Math.max(0, 1 - (now - t.diedAt) / LINGER_SECONDS);
    const headR = FIREBALL.radius * t.scale * fade;

    // Resample the recorded path at even intervals so the outline has enough
    // points for tongues of flame, then compute a normal and age per point.
    const oldest = pts[pts.length - 1]!;
    const maxAge = Math.max(0.05, (now - oldest.t) / TRAIL_SECONDS);
    const N = 28;
    const frames: Array<{ x: number; y: number; nx: number; ny: number; age: number }> = [];
    for (let k = 0; k < N; k++) {
      const age = (k / (N - 1)) * maxAge;
      const at = now - age * TRAIL_SECONDS;
      // Find the two samples bracketing `at` (pts are newest first).
      let i = 0;
      while (i < pts.length - 2 && pts[i + 1]!.t > at) i++;
      const a = pts[i]!;
      const b = pts[Math.min(i + 1, pts.length - 1)]!;
      const span = a.t - b.t;
      const w = span > 0 ? Math.min(1, Math.max(0, (a.t - at) / span)) : 0;
      const x = a.x + (b.x - a.x) * w;
      const y = a.y + (b.y - a.y) * w;
      let dx = a.x - b.x;
      let dy = a.y - b.y;
      const len = Math.hypot(dx, dy) || 1;
      dx /= len;
      dy /= len;
      frames.push({ x, y, nx: -dy, ny: dx, age });
    }

    // Shape is normalised to the trail's *current* length (maxAge) so a
    // just-spawned trail is a small complete flame, not a chopped-off block.
    // Silhouette: flush with the head, a bulge just behind it, then a taper.
    const width = (u: number): number => {
      const bulge = 1 + 0.55 * Math.exp(-Math.pow((u - 0.28) / 0.22, 2));
      return headR * 1.05 * bulge * Math.pow(1 - u, 0.45);
    };
    // Tongues: a triangle wave that cuts notches *into* the tail (valleys at
    // 0.45×, tips at 1.35×), growing toward the tip, travelling backwards,
    // alternating per edge. Suppressed while the trail is still short.
    const grown = Math.min(1, maxAge * 1.6);
    const spike = (v: number): number => 1 - Math.abs(2 * (v - Math.floor(v)) - 1);
    const tongue = (u: number, phase: number): number => {
      const depth = 0.9 * Math.pow(u, 0.9) * grown;
      return 1 - depth * 0.5 + depth * spike(u * 3 - now * 3.5 + phase);
    };

    // Build a closed ribbon covering the first `reach` of the trail, `mul` times the base width.
    const ribbon = (reach: number, mul: number, phaseL: number, phaseR: number): void => {
      const left: Array<[number, number]> = [];
      const right: Array<[number, number]> = [];
      let tip: [number, number] | null = null;
      for (const f of frames) {
        const u = f.age / maxAge; // 0 head … 1 tip of the whole trail
        if (u > reach) break;
        const local = u / reach; // 0..1 within this band so every band tapers to its own tip
        const w = width(u) * mul * Math.pow(1 - local, 0.3);
        const wl = Math.max(0.3, w * tongue(u, phaseL));
        const wr = Math.max(0.3, w * tongue(u, phaseR + 0.5));
        left.push([f.x + f.nx * wl, f.y + f.ny * wl]);
        right.push([f.x - f.nx * wr, f.y - f.ny * wr]);
        tip = [f.x, f.y];
      }
      if (left.length < 2 || !tip) return;
      ctx.beginPath();
      ctx.moveTo(left[0]![0], left[0]![1]);
      for (let i = 1; i < left.length; i++) ctx.lineTo(left[i]![0], left[i]![1]);
      ctx.lineTo(tip[0], tip[1]);
      for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i]![0], right[i]![1]);
      ctx.closePath();
    };

    const s = t.seed;
    ctx.globalAlpha = fade;
    // Coral tips (full length) with the ink edge, orange body, butter core nearest the ball.
    ribbon(1, 1, s, s + 2.1);
    ctx.fillStyle = EMBER;
    ctx.fill();
    inkStroke(ctx, 1.6, cam.scale, INK);
    ctx.globalAlpha = fade * 0.85;
    ctx.stroke();
    ctx.globalAlpha = fade;
    ribbon(0.78, 0.74, s + 0.8, s + 2.9);
    ctx.fillStyle = FIRE;
    ctx.fill();
    ribbon(0.48, 0.44, s + 1.6, s + 3.7);
    ctx.fillStyle = CORE;
    ctx.fill();
    ctx.globalAlpha = 1;
  }
}
